import React, { FC, useCallback, useState } from 'react';
import { t } from 'i18next';
import { DateTime } from 'luxon';

import { Box, Popover, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import SyncIcon from '@mui/icons-material/Sync';

import { DATE_TIME_FORMAT, formatDate } from 'utils/dates';

interface SyncJob {
  status?: string;
  finishedOn?: number;
  processedOn?: number;
  failedReason?: string;
}

interface SyncStatusPopoverProps {
  syncJob: SyncJob;
}

const getStatusIcon = (status?: string) => {
  const iconProps = { fontSize: 'small' as const, sx: { cursor: 'pointer' } };
  switch (status) {
    case 'completed':
      return <CheckCircleIcon {...iconProps} color="success" />;
    case 'failed':
    case 'delayed':
      return <ErrorIcon {...iconProps} color="error" />;
    case 'active':
      return <SyncIcon {...iconProps} color="info" />;
    case 'waiting':
      return <HourglassEmptyIcon {...iconProps} color="warning" />;
    case 'paused':
      return <PauseCircleIcon {...iconProps} color="warning" />;
    default:
      return <HourglassEmptyIcon {...iconProps} color="disabled" />;
  }
};

const styles = {
  root: { display: 'inline-flex', alignItems: 'center' },
  popover: { pointerEvents: 'none' },
  popoverContent: { p: 2 },
};

const anchorOptions = {
  vertical: 'bottom',
  horizontal: 'left',
} as const;

const SyncStatusPopover: FC<SyncStatusPopoverProps> = ({ syncJob }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Box onMouseEnter={onMouseEnter} onMouseLeave={onClose} sx={styles.root}>
        {getStatusIcon(syncJob.status)}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOptions}
        disableRestoreFocus
        sx={styles.popover}
      >
        <Box sx={styles.popoverContent}>
          <Typography variant="body2">
            <strong>{t('MUNKAVEGZOK.LAST_SYNC') as string}:</strong>{' '}
            {t(`MUNKAVEGZOK.SYNC_STATUS.${syncJob.status?.toUpperCase()}`) as string}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {syncJob.finishedOn || syncJob.processedOn
              ? formatDate(
                  DateTime.fromMillis((syncJob.finishedOn || syncJob.processedOn) as number),
                  DATE_TIME_FORMAT
                )
              : '-'}
          </Typography>
          {syncJob.failedReason ? (
            <Typography variant="body2" color="error">
              {syncJob.failedReason}
            </Typography>
          ) : null}
        </Box>
      </Popover>
    </>
  );
};

export default SyncStatusPopover;
